<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import Dropdown from 'primevue/dropdown'
import type { Quality } from '@/types/supplier-types'
import { useField, useFieldArray } from 'vee-validate'
import InputGroup from 'primevue/inputgroup'
import type { Roll } from '@/types/batch-types'
import { unitOptions } from '@/constants/dimension'
import { getHighestRollNumber } from '@/api/rolls'

const props = withDefaults(
  defineProps<{
    quality?: Quality
    formPath: string
    includeLength?: boolean
    includeMergeNo?: boolean
    includeSupplierNo?: boolean
    allowAddRolls?: boolean
    lengthLabel?: string
    lengthUnitLabel?: string
    weightLabel?: string
    errors: any
    standardBatchSize: number | undefined
  }>(),
  {
    includeLength: true,
    includeMergeNo: false,
    includeSupplierNo: false,
    allowAddRolls: false,
    lengthLabel: 'Length',
    lengthUnitLabel: 'Unit',
    weightLabel: 'Weight'
  }
)

const emit = defineEmits(['updateRolls'])

const { value, errorMessage } = useField<number>(`${props.formPath}.numRolls`)
const { push, remove, fields } = useFieldArray(`${props.formPath}.rolls`)
const { value: numRolls } = useField<number>(`${props.formPath}.numRolls`)
const localNumRolls = ref(value.value)
const nextRollNumberStart = ref<number>(1)

const confirmRolls = () => {
  value.value = localNumRolls.value
  disableInput.value = true
}

const fetchHighestRollNumberStart = async () => {
  try {
    const response = await getHighestRollNumber()
    nextRollNumberStart.value = response
  } catch (error) {
    console.error('Error fetching highest roll number start:', error)
  }
}

onMounted(async () => {
  await fetchHighestRollNumberStart()
  if (numRolls.value === undefined) numRolls.value = 1
  value.value = props.standardBatchSize !== undefined ? props.standardBatchSize : 1
  for (let i = 0; i < value.value; i++) {
    push({
      rollNumber: nextRollNumberStart.value + i,
      supplierRollNo: null,
      lengthValue: null,
      lengthUnit: null,
      weight: null,
      mergeNumber: ''
    })
  }
})

watch(
  fields,
  (newFields) => {
    emit(
      'updateRolls',
      newFields.map((f) => f.value as Roll)
    )
  },
  { deep: true }
)

watch(
  () => props.quality,
  (newQuality: Quality | undefined) => {
    cancelCustomInput()
  }
)

watch(
  () => value.value,
  (newNumRolls, oldNumRolls) => {
    if (newNumRolls > oldNumRolls) {
      for (let i = oldNumRolls; i < newNumRolls; i++) {
        push({
          rollNumber: nextRollNumberStart.value + i,
          supplierRollNo: null,
          lengthValue: null,
          lengthUnit: null,
          weight: null,
          mergeNumber: ''
        })
      }
    } else if (newNumRolls < oldNumRolls) {
      for (let i = oldNumRolls; i > newNumRolls; i--) {
        remove(i - 1)
      }
    }
  }
)

watch(
  () => props.standardBatchSize,
  (newSize) => {
    if (newSize !== undefined) {
      value.value = newSize
    }
  },
  { immediate: true }
)

const disableInput = ref(true)

const toggleInputLabel = computed(() => (disableInput.value ? 'Change this' : 'Cancel'))

const toggleInput = () => {
  disableInput.value = false
  localNumRolls.value = value.value
}

const cancelCustomInput = () => {
  disableInput.value = true
  localNumRolls.value = value.value // TODO: Pull this from the selected batch instead - determined by quality?
}

watch(
  () => value.value,
  (newVal) => {
    localNumRolls.value = newVal
  }
)
</script>

<template>
  <div>
    <div v-if="allowAddRolls" class="grid grid-cols-12 gap-10">
      <div class="col-span-6 pr-5 mb-3">
        <h2 class="font-semibold text-2xl">Add Rolls to this batch</h2>

        <label>Number of Rolls:</label>
        <InputGroup>
          <InputNumber
            :disabled="disableInput"
            v-model="localNumRolls"
            :invalid="!!errorMessage"
            :use-grouping="false"
            :min-fraction-digits="0"
            :max-fraction-digits="0"
          />
          <div v-if="disableInput">
            <Button :label="toggleInputLabel" @click="toggleInput" />
          </div>
          <div v-else class="flex gap">
            <Button label="Okay" @click="confirmRolls" />
            <Button label="Cancel" @click="cancelCustomInput" severity="secondary" outlined />
          </div>
        </InputGroup>
        <div class="flex items-center">
          <small class="italic">Determined by Quality chosen.</small>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div v-for="(field, index) in fields" :key="index" class="col-span-12 xl:col-span-6">
        <h3 class="font-medium">Roll {{ index + 1 }}:</h3>

        <div class="flex gap-2">
          <div class="flex flex-col">
            <label :for="`rol${index + 1}`" class="text-xs">Roll No.</label>
            <InputNumber
              v-model="(field.value as Roll).rollNumber"
              :id="`rol${index + 1}`"
              input-class="w-28 h-8"
              :invalid="!!props.errors[`${props.formPath}.rolls[${index}].rollNo`]"
              :use-grouping="false"
              :min-fraction-digits="0"
              :max-fraction-digits="0"
            />
          </div>
          <div v-if="includeSupplierNo" class="flex flex-col">
            <label :for="`rol${index + 1}`" class="text-xs">Supplier's Roll No.</label>
            <InputNumber
              v-model="(field.value as Roll).supplierRollNo"
              :id="`rol${index + 1}`"
              input-class="w-28 h-8"
              :invalid="!!props.errors[`${props.formPath}.rolls[${index}].supplierRollNo`]"
              :use-grouping="false"
              :min-fraction-digits="0"
              :max-fraction-digits="0"
            />
          </div>
          <div v-if="includeLength" class="flex items-center w-full">
            <div class="flex flex-col w-full">
              <label :for="`rol${index + 1}`" class="text-xs">{{ lengthLabel }}</label>
              <InputNumber
                v-model="(field.value as Roll).lengthValue"
                :id="`rol${index + 1}`"
                input-class="w-28 h-8"
                :invalid="!!props.errors[`${props.formPath}.rolls[${index}].length`]"
                :use-grouping="false"
                :min-fraction-digits="0"
                :max-fraction-digits="2"
              />
            </div>
            <div class="flex flex-col w-1/4">
              <label :for="`roll:${index + 1}`" class="text-xs">{{ lengthUnitLabel }}</label>
              <Dropdown
                :id="`roll${index + 1}`"
                input-class="w-28 h-8"
                v-model="(field.value as Roll).lengthUnit"
                :options="unitOptions"
                optionLabel="label"
                optionValue="value"
                :invalid="!!props.errors[`${props.formPath}.rolls[${index}].lengthUnit`]"
                type="number"
              />
            </div>
          </div>
          <div class="flex flex-col">
            <label :for="`rol${index + 1}`" class="text-xs">{{ weightLabel }}</label>
            <InputNumber
              v-model="(field.value as Roll).weight"
              :id="`rol${index + 1}`"
              input-class="w-28 h-8"
              :invalid="!!props.errors[`${props.formPath}.rolls[${index}].weight`]"
              :use-grouping="false"
              :min-fraction-digits="0"
              :max-fraction-digits="3"
            />
          </div>
          <div v-if="includeMergeNo" class="flex flex-col">
            <label :for="`rol${index + 1}`" class="text-xs">Merge No.</label>
            <InputNumber
              v-model="(field.value as Roll).mergeNumber"
              :id="`rol${index + 1}`"
              input-class="w-28 h-8"
              :invalid="!!props.errors[`${props.formPath}.rolls[${index}].mergeNumber`]"
              :use-grouping="false"
              :min-fraction-digits="0"
              :max-fraction-digits="0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>